import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Github, ArrowLeft } from 'lucide-react';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOAuthLogin = (provider) => {
    // Directly redirect to backend OAuth endpoint
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${API_URL}/api/auth/oauth/${provider.toLowerCase()}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white relative">
        {/* Back to Home Button */}
        <Link 
          to="/" 
          className="absolute top-8 left-8 flex items-center gap-2 text-[#6b7280] hover:text-[#e57f3f] transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Back to Home</span>
        </Link>

        <div className="w-full max-w-md space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-manrope font-bold text-[#111827] mb-2">
              Welcome Back
            </h1>
            <p className="text-[#6b7280]">
              Access your account and continue building
            </p>
          </div>

          {/* OAuth Buttons */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-2 hover:bg-gray-50 transition-colors"
              onClick={() => handleOAuthLogin('Google')}
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-2 hover:bg-gray-50 transition-colors"
              onClick={() => handleOAuthLogin('GitHub')}
              data-testid="github-login-button"
            >
              <Github className="w-5 h-5 mr-3" />
              Continue with GitHub
            </Button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-[#6b7280]">Or continue with</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#111827] font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#111827] font-medium">
                  Password
                </Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#e57f3f] hover:text-[#c43408] transition-colors"
                >
                  Reset password
                </Link>
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                required
                className="h-12"
                data-testid="password-input"
              />
            </div>

            <div className="flex items-center">
              <input
                id="remember"
                type="checkbox"
                className="h-4 w-4 text-[#e57f3f] border-gray-300 rounded focus:ring-[#e57f3f]"
              />
              <label htmlFor="remember" className="ml-2 text-sm text-[#6b7280]">
                Keep me signed in
              </label>
            </div>

            <Button
              type="submit"
              className="w-full h-12 gradient-primary text-white text-base font-semibold hover:opacity-90 transition-opacity"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="text-center text-sm">
            <span className="text-[#6b7280]">New to our platform? </span>
            <Link 
              to="/signup" 
              className="text-[#e57f3f] hover:text-[#c43408] font-semibold transition-colors"
              data-testid="signup-link"
            >
              Create Account
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Brand Visual */}
      <div className="hidden lg:flex lg:w-1/2 gradient-primary items-center justify-center p-12 relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        
        {/* Content */}
        <div className="relative z-10 text-white text-center max-w-lg">
          <img 
            src="https://customer-assets.emergentagent.com/job_create-skills/artifacts/kzrw28ys_Skillerate%20Icon%20White.svg" 
            alt="Skillerate" 
            className="h-32 w-32 mx-auto mb-8"
          />
          <h2 className="text-5xl font-manrope font-bold mb-6 leading-tight">
            Skillerate
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Build, showcase, and share quality Claude Skills. Join creators building the future of AI.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;